import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { STAGES, OPEN_STAGES, STAGE_COLORS } from "../lib/fields";
import { DataTable } from "../components/DataTable";
import { EntityForm } from "../components/EntityForm";
import { OpportunityDetailDialog } from "../components/OpportunityDetailDialog";
import { useOpenById } from "../lib/useOpenById";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Flag, LayoutGrid, Table2 } from "lucide-react";

export default function Opportunities() {
  const { t, canWrite, plLabel, plOptions } = useApp();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("kanban");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [closing, setClosing] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [dragged, setDragged] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/opportunities?limit=500").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new") === "1") { setEditing({}); setParams({}, { replace: true }); }
  }, [params, setParams]);
  useOpenById("opportunities", setDetail);

  const changeStage = async (opp, stage, reason) => {
    try {
      await api.put(`/opportunities/${opp.id}/stage`, { stage, close_reason: reason });
      toast.success(`${opp.name} → ${plLabel("opp_stages", stage)}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const onDrop = (stage) => {
    if (!dragged || dragged.stage === stage) return;
    if (stage === "Closed Won" || stage === "Closed Lost") {
      setClosing({ opp: dragged, stage });
    } else {
      changeStage(dragged, stage);
    }
    setDragged(null);
  };

  const weighted = rows.filter((o) => !o.stage?.startsWith("Closed"))
    .reduce((s, o) => s + (o.weighted_amount || 0), 0);

  const columns = [
    { key: "name", label: t("name"), render: (r) => (
      <span className="font-medium text-[#1B2A4A] flex items-center gap-1.5">
        {r.aging_flag && <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" />}{r.name}
      </span>) },
    { key: "account_name", label: t("account") },
    { key: "stage", label: t("stage"), render: (r) => <Badge className={`${STAGE_COLORS[r.stage] || ""} hover:bg-inherit`}>{plLabel("opp_stages", r.stage)}</Badge> },
    { key: "amount", label: t("amount"), align: "right", render: (r) => fmtEur(r.amount, r.currency) },
    { key: "probability", label: "%", align: "right", render: (r) => `${r.probability}%` },
    { key: "weighted_amount", label: t("weighted_forecast"), align: "right", render: (r) => fmtEur(r.weighted_amount) },
    { key: "product_type", label: t("product_type") },
    { key: "business_type", label: t("business_type") },
    { key: "close_date", label: t("close_date") },
    { key: "owner_name", label: t("owner") },
  ];

  const kanbanStages = [...OPEN_STAGES, "Closed Won", "Closed Lost"];

  return (
    <div className="space-y-4" data-testid="opportunities-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("opportunities")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("weighted_forecast")}: <span className="font-mono font-semibold text-[#1B2A4A]">{fmtEur(weighted)}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-gray-200 overflow-hidden">
            <button data-testid="view-kanban-btn" onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors duration-150 ${view === "kanban" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <LayoutGrid className="h-3.5 w-3.5" />{t("kanban")}
            </button>
            <button data-testid="view-table-btn" onClick={() => setView("table")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors duration-150 ${view === "table" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <Table2 className="h-3.5 w-3.5" />{t("table")}
            </button>
          </div>
          {canWrite && (
            <Button data-testid="opportunities-new-btn" onClick={() => setEditing({})}
              className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new")}
            </Button>
          )}
        </div>
      </div>

      {view === "kanban" ? (
        <div className="flex overflow-x-auto gap-4 pb-4" data-testid="opportunities-kanban">
          {kanbanStages.map((stage) => {
            const items = rows.filter((o) => o.stage === stage);
            const total = items.reduce((s, o) => s + (Number(o.amount) || 0), 0);
            return (
              <div key={stage} className="w-72 shrink-0 bg-gray-100/80 rounded-lg p-3 flex flex-col"
                data-testid={`kanban-col-${stage.replace(/[^a-zA-Z]/g, "")}`}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage)}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-600">{plLabel("opp_stages", stage)}</span>
                  <span className="text-[10px] font-mono text-gray-400">{items.length} · {fmtEur(total)}</span>
                </div>
                <div className="space-y-2 flex-1 min-h-[120px]">
                  {items.map((o) => (
                    <div key={o.id} draggable={canWrite} onDragStart={() => setDragged(o)} onClick={() => setDetail(o)}
                      data-testid="kanban-card"
                      className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform duration-150">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 leading-snug">{o.name}</p>
                        {o.aging_flag && <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" data-testid="aging-flag" />}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{o.account_name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-mono font-semibold text-[#1B2A4A]">{fmtEur(o.amount, o.currency)}</span>
                        <span className="text-[10px] text-gray-400 font-mono">{o.probability}%</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {o.product_type && <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{o.product_type}</span>}
                        {o.business_type && <span className="text-[10px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">{o.business_type}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <DataTable module="opportunities" rows={rows} columns={columns} onRowClick={setDetail} onImported={load} />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("create")} – {t("opportunities")}</DialogTitle></DialogHeader>
          {editing && <EntityForm module="opportunities" initial={editing}
            onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>

      {detail && (
        <OpportunityDetailDialog opportunity={detail}
          onClose={() => setDetail(null)} onChanged={load} />
      )}

      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{plLabel("opp_stages", closing?.stage)} – {closing?.opp?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500">{t("required_close_reason")}</p>
          <Select value={closeReason} onValueChange={setCloseReason}>
            <SelectTrigger data-testid="close-reason-select"><SelectValue placeholder={t("close_reason")} /></SelectTrigger>
            <SelectContent>{plOptions("close_reasons").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={!closeReason} data-testid="close-confirm-btn"
            onClick={() => { changeStage(closing.opp, closing.stage, closeReason); setClosing(null); setCloseReason(""); }}>
            {t("save")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
